import { useCurrentFrame, useVideoConfig } from 'remotion';
import React, { useMemo } from 'react';

import { SegmentTransitionVolumeProvider } from './SegmentTransitionVolumeContext';
import { SegmentBoundaryTransition } from '@/App/remotion/MainPlayer/types/Track/SegmentTransition';
import { getSegmentTransitionDefinition } from '@/App/remotion/libs/transitions/segmentTransition/segmentTransitionRegistry';
import { hasAudioDip } from '@/App/remotion/libs/transitions/segmentTransition/utils';

export const SegmentTransitionWrapper: React.FC<{
  segmentId: string;
  segmentDurationInFrames: number;
  side: 'out' | 'in';
  incomingTransitionForVolume?: SegmentBoundaryTransition | null;
  outgoingTransitionForVolume?: SegmentBoundaryTransition | null;
  children: React.ReactNode;
}> = ({
  segmentId: _segmentId,
  segmentDurationInFrames,
  side,
  incomingTransitionForVolume = null,
  outgoingTransitionForVolume = null,
  children
}) => {
  const transition = useMemo(
    () => (side === 'in' ? incomingTransitionForVolume : outgoingTransitionForVolume),
    [side, incomingTransitionForVolume, outgoingTransitionForVolume]
  );
  const needsVolumeProvider = hasAudioDip(incomingTransitionForVolume) || hasAudioDip(outgoingTransitionForVolume);
  const isOutermost =
    side === 'out' || outgoingTransitionForVolume === null || outgoingTransitionForVolume === undefined;

  const currentFrame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const definition = transition ? getSegmentTransitionDefinition(transition.transitionType) : undefined;

  const styles = useMemo(() => {
    if (!transition || !definition) {
      return null;
    }
    return definition.getStyles({
      currentFrame,
      segmentDurationInFrames,
      transition,
      side,
      fps,
      width,
      height
    });
  }, [transition, definition, currentFrame, segmentDurationInFrames, side, fps, width, height]);

  const contentStyle: React.CSSProperties = useMemo(
    () => ({
      opacity: styles?.contentOpacity,
      transform: styles?.transform,
      clipPath: styles?.clipPath,
      filter: styles?.filter,
      width: '100%',
      height: '100%'
    }),
    [styles?.contentOpacity, styles?.transform, styles?.clipPath, styles?.filter]
  );

  const wrapWithVolumeProvider = needsVolumeProvider && isOutermost;

  if (!transition || !definition || !styles) {
    if (wrapWithVolumeProvider) {
      return (
        <SegmentTransitionVolumeProvider
          segmentDurationInFrames={segmentDurationInFrames}
          incomingTransition={incomingTransitionForVolume}
          outgoingTransition={outgoingTransitionForVolume}
        >
          {children}
        </SegmentTransitionVolumeProvider>
      );
    }
    return <>{children}</>;
  }

  const transitionContent = (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div data-testid="segment-transition-content" style={contentStyle}>
        {children}
      </div>

      {styles?.overlayOpacity !== undefined && styles?.overlayOpacity > 0 && (
        <div
          data-testid="segment-transition-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'black',
            opacity: styles.overlayOpacity,
            pointerEvents: 'none',
            zIndex: 1000
          }}
        />
      )}
    </div>
  );

  if (wrapWithVolumeProvider) {
    return (
      <SegmentTransitionVolumeProvider
        segmentDurationInFrames={segmentDurationInFrames}
        incomingTransition={incomingTransitionForVolume}
        outgoingTransition={outgoingTransitionForVolume}
      >
        {transitionContent}
      </SegmentTransitionVolumeProvider>
    );
  }

  return transitionContent;
};
